import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import { IdentityService, PosAdminService, SelfcarePassService } from '@yol-digital/ms-client';
import jwtDecode from 'jwt-decode';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { ENVIRONMENT_URLS_CONFIG_TOKEN } from 'app-initialization';
import { BrandService } from 'brand';
import { FeatureFlagService } from 'feature-flag';
import { LanguageService } from 'language';
import { ToastService } from 'toast';
import { TranslateService } from 'translate';
import { formatMsisdn, ObservableCachedDataLoaderService, StateService, StorageKeys, StorageService } from 'utils';
import { AgentService } from './agent.service';
import { AuthCompleteResponse, DataFromAccessToken } from './interfaces/auth.model';
import Api = IdentityService.Api;
type SecretCodeResp = IdentityService.SecretCodeResp;
type PosSecretCodeResp = IdentityService.PosSecretCodeResp;
type MethodEnum = IdentityService.MethodEnum;
type BrandEnum = IdentityService.BrandEnum;
type LanguageEnum = IdentityService.LanguageEnum;
type SelfcareLoginReq = IdentityService.SelfcareLoginReq;
type CodeVerificationReq = IdentityService.CodeVerificationReq;
type AccessTokenResp = IdentityService.AccessTokenResp;
type PosCredentialLoginReq = IdentityService.PosCredentialLoginReq;
type PosCodeVerificationReq = IdentityService.PosCodeVerificationReq;
type UpdatePhoneNumberConfirmResp = PosAdminService.UpdatePhoneNumberConfirmResp;
type UpdatePhoneNumberConfirmReq = PosAdminService.UpdatePhoneNumberConfirmReq;
import PosAdminApi = PosAdminService.Api;
type SelfcareMethodEnum = SelfcarePassService.MethodEnum;

export interface RequestCodePayload {
  value: string;
  method: MethodEnum;
  password?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private brandService = inject(BrandService);
  private languageService = inject(LanguageService);
  private stateService = inject(StateService);
  private agentService = inject(AgentService);
  private translateService = inject(TranslateService);
  private toastService = inject(ToastService);
  private dataLoader = inject(ObservableCachedDataLoaderService);
  private featureFlagService = inject(FeatureFlagService);
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private storageService = inject(StorageService);
  private api: Api;
  private posAdminApi: PosAdminApi;
  private secretCode: string;
  private refId: string;
  private oidcEndpoint: string;
  private selfcarePassApi: SelfcarePassService.Api;
  public selfcarePassError = signal<string>(null);
  public loginValue = signal('');
  public customSelfcarePassErrors = [
    'ERR_BAD_REQUEST',
    'ERR_INVALID_CODE',
    'ERR_ACCOUNT_NOT_FOUND',
    'ERR_MAX_REACHED',
    'ERR_SYSTEM_ERROR',
    'ERR_MULTIPLE_ACCOUNT_FOUND',
    'ERR_LINK_ALREADY_EXPIRED_OR_WRONG',
  ];

  constructor() {
    const http = this.http;
    const config = inject(ENVIRONMENT_URLS_CONFIG_TOKEN);

    this.api = new Api(config.newMicroServiceEndpoint, http);
    this.posAdminApi = new PosAdminApi(config.newMicroServiceEndpoint, http);
    this.oidcEndpoint = config.newMicroServiceEndpoint + '/oidc';
    this.selfcarePassApi = new SelfcarePassService.Api(config.newMicroServiceEndpoint, http);
  }

  public get isMfaEnabled(): boolean {
    return this.storageService.get(StorageKeys.MfaEnabled) || false;
  }

  public getApiEndpoint(isTv: boolean) {
    if (isTv) {
      return this.api.tv;
    }
    return this.api.selfcare;
  }

  public login(payload: RequestCodePayload, isTv = false): Observable<SecretCodeResp> {
    const { method, password } = payload;
    const value = payload.value.replace(/\s+/g, '');
    const apiEndpoint = this.getApiEndpoint(isTv);
    const params: SelfcareLoginReq = {
      method,
      brand: this.brandService.brand as BrandEnum,
      value,
      language: this.languageService.current as LanguageEnum,
    };
    this.loginValue.set(method === 'phone' ? formatMsisdn(value) : value);
    if (password) {
      params.password = password;
    }

    return apiEndpoint.login(params).pipe(
      tap((user: SecretCodeResp) => {
        if (user) {
          this.secretCode = user.secret;
        }
      })
    );
  }

  public handleSelfcarePassError(err: HttpErrorResponse): boolean {
    let shouldDisableButton = false;
    const error = err.error.error;

    if (this.customSelfcarePassErrors.includes(error)) {
      let toastSeconds = 3000;
      if (error === 'ERR_LINK_ALREADY_EXPIRED_OR_WRONG') {
        shouldDisableButton = true;
        toastSeconds = 5000;
        setTimeout(() => {
          this.router.navigate(['auth', 'login']);
        }, 5000);
      }
      this.selfcarePassError.set(error.toUpperCase());
      this.toastService.add(
        this.translateService.getTranslation(['password', 'errors', this.selfcarePassError()]),
        false,
        toastSeconds
      );
    }
    return shouldDisableButton;
  }

  public validate(code: string, isTv = false): Observable<AccessTokenResp> {
    const apiEndpoint = this.getApiEndpoint(isTv);
    const payload: CodeVerificationReq = {
      secret: this.secretCode,
      code,
    };

    return apiEndpoint.validate(payload).pipe(
      tap((response: AccessTokenResp) => {
        const data: DataFromAccessToken = jwtDecode(response.accessToken);
        const accountId: string = data.sub;
        this.storageService.set(StorageKeys.MfaEnabled, data.mfaEnabled);
        this.storeUserAuth(response.accessToken, accountId);
      })
    );
  }

  public logout(): Observable<void | { error: string }> {
    return this.api.selfcare.logout().pipe(
      tap(() => {
        this.dataLoader.remove(StorageKeys.SelfCareToken);
        this.storageService.remove(StorageKeys.MfaEnabled);
        this.featureFlagService.setAttributeOverrides({ loggedIn: false });
      })
    );
  }

  public invalidateAllSessions(): Observable<void | { error: string }> {
    return this.api.selfcare.logoutAll();
  }

  public authComplete(oidcJwt: string): Observable<AuthCompleteResponse> {
    return this.getToken().pipe(
      switchMap(accessToken => {
        if (!accessToken) return of(null);
        let headers = new HttpHeaders();
        headers = headers.set('Oidc-Jwt', oidcJwt).set('Selfcare-Jwt', accessToken);
        return this.http.post<AuthCompleteResponse>(`${this.oidcEndpoint}/auth/complete`, {}, { headers });
      })
    );
  }

  public loginPos(
    username: PosCredentialLoginReq['username'],
    password: PosCredentialLoginReq['password'],
    tempChannel?: PosCredentialLoginReq['tempChannel']
  ): Observable<PosSecretCodeResp> {
    const paramsPos: PosCredentialLoginReq = {
      username,
      password,
      brand: this.brandService.brand as BrandEnum,
      ...(tempChannel ? { tempChannel } : {}),
    };

    return this.api.pos.login(paramsPos).pipe(
      tap((user: PosSecretCodeResp) => {
        if (user) {
          this.secretCode = user.secret;
        }
      })
    );
  }

  public validatePos(secret: string, code?: string): Observable<DataFromAccessToken> {
    const payload: PosCodeVerificationReq = {
      secret,
      code,
    };
    return this.api.pos.validate(payload).pipe(
      map((response: AccessTokenResp) => {
        const decodedData: DataFromAccessToken = jwtDecode(response.accessToken);
        this.agentService.setAgentDetails(jwtDecode(response.accessToken));
        this.storeAgentAuth(response.accessToken);
        return decodedData; // Return only the decoded data
      })
    );
  }

  public updatePhoneNumberPosStart(phoneNumber: string): Observable<PosAdminService.UpdatePhoneNumberStartResp> {
    return this.posAdminApi.public.updateStart({ phoneNumber }).pipe(
      tap((response: PosAdminService.UpdatePhoneNumberStartResp) => {
        if (response.refId) {
          this.refId = response.refId;
        }
      })
    );
  }

  public updatePhoneNumberPosConfirm(code: string): Observable<
    | UpdatePhoneNumberConfirmResp
    | {
        error: string;
      }
  > {
    const payload: UpdatePhoneNumberConfirmReq = {
      refId: this.refId,
      code,
    };

    return this.posAdminApi.public.confirm(payload);
  }

  public getTokenPos(): Observable<string | false | null> {
    if (!isPlatformBrowser(this.platformId)) return of(null);
    return this.dataLoader
      .get(StorageKeys.Pos, () => {
        return this.api.pos.refreshToken().pipe(
          map((resp: AccessTokenResp) => {
            this.agentService.setAgentDetails(jwtDecode(resp.accessToken));
            return resp.accessToken;
          }),
          catchError(() => of(false as const))
        );
      })
      .pipe(
        switchMap(token => {
          if (!token) return of(false as const);
          if (this.validateToken(token)) return of(token);

          this.dataLoader.remove(StorageKeys.Pos);
          return this.getTokenPos();
        })
      );
  }

  public logoutPos(): Observable<void | { error: string }> {
    return this.api.pos.logout().pipe(
      tap(() => {
        this.dataLoader.remove(StorageKeys.Pos);
      })
    );
  }

  public setPassword(password: string) {
    return this.selfcarePassApi.public
      .setPassword({ password })
      .pipe(tap(() => this.storageService.set(StorageKeys.MfaEnabled, true)));
  }

  public requestResetPassword(method: SelfcareMethodEnum, value: string) {
    return this.selfcarePassApi.public.requestResetPassword({
      method,
      value,
      language: this.languageService.current as LanguageEnum,
    });
  }

  public resetPassword(refId: string, newPassword: string) {
    return this.selfcarePassApi.public.resetPassword({
      refId,
      newPassword,
    });
  }

  public getToken(): Observable<string | false | null> {
    if (!isPlatformBrowser(this.platformId)) return of(null);
    return this.dataLoader
      .get(StorageKeys.SelfCareToken, () => {
        return this.api.selfcare.refreshToken().pipe(
          map(resp => {
            if ('error' in resp) throw new Error(resp.error);
            return resp.accessToken;
          }),
          catchError(() => of(false as const))
        );
      })
      .pipe(
        switchMap(token => {
          if (!token) return of(false as const);
          if (this.validateToken(token)) return of(token);

          this.dataLoader.remove(StorageKeys.SelfCareToken);
          return this.getToken();
        })
      );
  }

  public isAuthenticated(): Observable<boolean> {
    return this.getToken().pipe(map(Boolean));
  }

  private validateToken(token: string): string | null {
    if (!token) return null;
    const data: DataFromAccessToken = jwtDecode(token);
    const expirationDate = data.jwtExpireUnixTime ?? data.exp;
    if (expirationDate > Date.now() / 1000) return token;
    return null;
  }

  private storeUserAuth(token: string, accountId: string): void {
    this.stateService.set(StorageKeys.AccountId, accountId);
    this.dataLoader.set(StorageKeys.SelfCareToken, token);
    this.featureFlagService.setAttributeOverrides({ loggedIn: true });
  }

  private storeAgentAuth(token: string): void {
    this.dataLoader.set(StorageKeys.Pos, token);
  }

  public changePassword(oldPassword: string, newPassword: string) {
    return this.selfcarePassApi.public.changePassword({
      oldPassword,
      newPassword,
    });
  }
}
